import { serverBaseURL } from "common/settings";

class ApiError extends Error {
  constructor(message, { status = 0, statusText = "", url = "", body = null, cause } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.url = url;
    this.body = body;

    if (cause) {
      this.cause = cause;
    }
  }
}

function buildApiUrl(path, { baseUrl = serverBaseURL, query } = {}) {
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const normalizedPath = String(path).replace(/^\/+/, "");
  const url = new URL(normalizedPath, normalizedBaseUrl);

  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    url.searchParams.delete(key);

    if (Array.isArray(value)) {
      value.forEach((item) => url.searchParams.append(key, String(item)));
    } else {
      url.searchParams.set(key, String(value));
    }
  });

  return url;
}

function isNativeRequestBody(body) {
  return (
    typeof body === "string" ||
    (typeof FormData !== "undefined" && body instanceof FormData) ||
    (typeof Blob !== "undefined" && body instanceof Blob) ||
    (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) ||
    (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer)
  );
}

function prepareRequestBody(body, headers) {
  if (body === undefined || isNativeRequestBody(body)) {
    return body;
  }

  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return JSON.stringify(body);
}

async function readResponseBody(response, responseType) {
  if (responseType === "none" || response.status === 204 || response.status === 205) {
    return null;
  }

  if (responseType === "blob") {
    return response.blob();
  }

  const text = await response.text();

  if (!text || responseType === "text") {
    return text || null;
  }

  const contentType = response.headers.get("Content-Type") ?? "";
  const normalizedText = text.trim();
  const looksLikeJson = normalizedText.startsWith("{") || normalizedText.startsWith("[");
  const shouldParseJson =
    responseType === "json" ||
    contentType.includes("json") ||
    (responseType === "auto" && looksLikeJson);

  if (!shouldParseJson) {
    return text;
  }

  return JSON.parse(normalizedText);
}

function getHttpErrorMessage(response, body) {
  if (body && typeof body === "object") {
    const detail = body.detail ?? body.message ?? body.error;

    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
  }

  return `Request failed (${response.status}${
    response.statusText ? ` ${response.statusText}` : ""
  }).`;
}

function createApiClient({
  getToken,
  baseUrl = serverBaseURL,
  fetchImpl = globalThis.fetch,
  onError,
} = {}) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("A fetch implementation is required.");
  }

  async function performRequest(
    path,
    {
      method = "GET",
      query,
      headers: customHeaders,
      body,
      responseType = "auto",
      auth = true,
      signal,
    } = {}
  ) {
    const url = buildApiUrl(path, { baseUrl, query });
    const headers = new Headers(customHeaders);

    if (!headers.has("Accept")) {
      headers.set("Accept", "application/json");
    }

    if (auth) {
      if (typeof getToken !== "function") {
        throw new ApiError("Authentication is not configured for this request.", {
          url: url.toString(),
        });
      }

      let accessToken;

      try {
        accessToken = await getToken();
      } catch (cause) {
        throw new ApiError("Unable to authenticate the request. Please sign in again.", {
          url: url.toString(),
          cause,
        });
      }

      if (!accessToken) {
        throw new ApiError("Your session has expired. Please sign in again.", {
          url: url.toString(),
        });
      }

      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    let response;

    try {
      response = await fetchImpl(url.toString(), {
        method,
        headers,
        body: prepareRequestBody(body, headers),
        signal,
      });
    } catch (cause) {
      throw new ApiError("Unable to connect to the server. Please try again.", {
        url: url.toString(),
        cause,
      });
    }

    let responseBody;

    try {
      responseBody = await readResponseBody(response, response.ok ? responseType : "auto");
    } catch (cause) {
      throw new ApiError("The server returned an invalid JSON response.", {
        status: response.status,
        statusText: response.statusText,
        url: url.toString(),
        cause,
      });
    }

    if (!response.ok) {
      throw new ApiError(getHttpErrorMessage(response, responseBody), {
        status: response.status,
        statusText: response.statusText,
        url: url.toString(),
        body: responseBody,
      });
    }

    return responseBody;
  }

  async function request(path, options) {
    try {
      return await performRequest(path, options);
    } catch (error) {
      if (typeof onError === "function") {
        onError(error);
      }

      throw error;
    }
  }

  return {
    request,
    get: (path, options) => request(path, { ...options, method: "GET" }),
    post: (path, options) => request(path, { ...options, method: "POST" }),
    put: (path, options) => request(path, { ...options, method: "PUT" }),
    delete: (path, options) => request(path, { ...options, method: "DELETE" }),
  };
}

export { ApiError, buildApiUrl, createApiClient };
